import React from 'react';
import type { TFunction } from 'i18next';
import type { Goal, Filters } from '../types';
import { Card, CardContent } from '../../../../components/ui/Card';

interface GoalListViewProps {
  filteredGoals: Goal[];
  visibleCounts: { company: number; team: number; individual: number };
  loading: boolean;
  filters: Filters;
  handleGoalClick: (goal: Goal) => void;
  loadMoreGoals: (level: string) => void;
  getStatusLabel: (status: string) => string;
  t: TFunction;
}

const statusColors: Record<string, { bg: string; text: string }> = {
  'completed': { bg: 'bg-success/10', text: 'text-success' },
  'on-track': { bg: 'bg-primary/10', text: 'text-primary' },
  'at-risk': { bg: 'bg-warning/10', text: 'text-warning' },
  'overdue': { bg: 'bg-error/10', text: 'text-error' },
};

function GoalCard({
  goal,
  borderColor,
  hoverShadow,
  showDepartment,
  handleGoalClick,
  getStatusLabel,
  t,
}: {
  goal: Goal;
  borderColor: string;
  hoverShadow: string;
  showDepartment: boolean;
  handleGoalClick: (goal: Goal) => void;
  getStatusLabel: (status: string) => string;
  t: TFunction;
}) {
  const colors = statusColors[goal.status] || statusColors['overdue'];

  return (
    <Card
      variant="interactive"
      data-testid={`list-goal-${goal.id}`}
      className={`${borderColor} hover:${hoverShadow} hover:-translate-y-0.5 transition-all duration-300 ${goal.isArchived ? 'opacity-60' : ''}`}
      onClick={() => handleGoalClick(goal)}
    >
      <CardContent className="p-3">
        <div className="flex justify-between mb-1">
          <h6 className="text-base font-medium">
            {goal.title}
            {goal.isArchived && (
              <span className="text-xs text-muted-foreground ms-1">
                {t('(Archived)')}
              </span>
            )}
          </h6>
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}>
            {getStatusLabel(goal.status)}
          </span>
        </div>
        <p className="text-sm text-muted-foreground mb-1">
          {goal.description.split('\n')[0]}
        </p>
        <div className="flex justify-between items-center">
          <span className="text-xs text-muted-foreground">
            {goal.owner}{showDepartment ? ` \u2022 ${goal.department}` : ''}
          </span>
          <span className="text-xs text-muted-foreground">
            {t('Due:')} {goal.dueDate}
          </span>
        </div>
        <div className="mt-1 bg-muted rounded-full h-2">
          <div
            className="bg-primary h-full rounded-full"
            style={{ width: `${goal.progress}%` }}
          />
        </div>
      </CardContent>
    </Card>
  );
}

interface GoalSectionProps {
  title: string;
  level: string;
  goals: Goal[];
  visibleCount: number;
  gradientIcon: React.ReactNode;
  borderColor: string;
  hoverShadow: string;
  showDepartment: boolean;
  handleGoalClick: (goal: Goal) => void;
  loadMoreGoals: (level: string) => void;
  getStatusLabel: (status: string) => string;
  t: TFunction;
}

function GoalSection({
  title,
  level,
  goals,
  visibleCount,
  gradientIcon,
  borderColor,
  hoverShadow,
  showDepartment,
  handleGoalClick,
  loadMoreGoals,
  getStatusLabel,
  t,
}: GoalSectionProps) {
  if (goals.length === 0) return null;

  return (
    <div className={level === 'individual' ? 'mb-4' : 'mb-6'}>
      <h6 className="mb-2 flex items-center gap-1 text-lg font-semibold">
        {gradientIcon}
        <span>{title}</span>
      </h6>
      <div className="grid gap-2">
        {goals.slice(0, visibleCount).map((goal) => (
          <GoalCard
            key={goal.id}
            goal={goal}
            borderColor={borderColor}
            hoverShadow={hoverShadow}
            showDepartment={showDepartment}
            handleGoalClick={handleGoalClick}
            getStatusLabel={getStatusLabel}
            t={t}
          />
        ))}
        {goals.length > visibleCount && (
          <button
            className="text-primary hover:text-primary/80 text-sm py-1"
            onClick={() => loadMoreGoals(level)}
            data-testid={`load-more-${level}`}
          >
            {t('Load More')} ({goals.length - visibleCount} {t('remaining')})
          </button>
        )}
      </div>
    </div>
  );
}

export default function GoalListView({
  filteredGoals,
  visibleCounts,
  loading,
  filters,
  handleGoalClick,
  loadMoreGoals,
  getStatusLabel,
  t,
}: GoalListViewProps) {
  const companyGoals = filteredGoals.filter(g => g.level === 'company');
  const teamGoals = filteredGoals.filter(g => g.level === 'team');
  const individualGoals = filteredGoals.filter(g => g.level === 'individual');

  return (
    <div className="flex-1 overflow-auto max-h-[75vh] p-3" data-testid="goal-list-view">
      <div className="max-w-screen-xl mx-auto">
        {loading ? (
          <div className="flex justify-center p-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : (
          <>
            <GoalSection
              title={t('Company Goals')}
              level="company"
              goals={companyGoals}
              visibleCount={visibleCounts.company}
              gradientIcon={
                <span className="inline-block w-4 h-4 rounded-sm" style={{ background: 'linear-gradient(135deg, #2563eb 0%, #8b5cf6 100%)' }} />
              }
              borderColor="border-s-4 border-s-primary"
              hoverShadow="shadow-[0_4px_12px_rgba(37,99,235,0.15)]"
              showDepartment={false}
              handleGoalClick={handleGoalClick}
              loadMoreGoals={loadMoreGoals}
              getStatusLabel={getStatusLabel}
              t={t}
            />

            <GoalSection
              title={t('Team Goals')}
              level="team"
              goals={teamGoals}
              visibleCount={visibleCounts.team}
              gradientIcon={
                <span className="inline-block w-4 h-4 rounded-sm" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #eab308 100%)' }} />
              }
              borderColor="border-s-4 border-s-warning"
              hoverShadow="shadow-[0_4px_12px_rgba(245,158,11,0.15)]"
              showDepartment={true}
              handleGoalClick={handleGoalClick}
              loadMoreGoals={loadMoreGoals}
              getStatusLabel={getStatusLabel}
              t={t}
            />

            <GoalSection
              title={t('Individual Goals')}
              level="individual"
              goals={individualGoals}
              visibleCount={visibleCounts.individual}
              gradientIcon={
                <span className="inline-block w-4 h-4 rounded-sm border-2 border-muted-foreground" />
              }
              borderColor="border-2 border-border"
              hoverShadow="shadow-[0_4px_12px_rgba(107,114,128,0.1)]"
              showDepartment={true}
              handleGoalClick={handleGoalClick}
              loadMoreGoals={loadMoreGoals}
              getStatusLabel={getStatusLabel}
              t={t}
            />

            {filteredGoals.length === 0 && !loading && (
              <Card variant="elevated">
                <CardContent className="p-4 text-center">
                  <p className="text-muted-foreground">
                    {t('No goals found.')} {filters.search ? t('Try a different search term.') : t('Create a new goal to get started.')}
                  </p>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}
